import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import Loading from '../../../shared/Loading';
import { tryToFetchLeoSubscriptions } from '../../../store/subscriptions/subscriptionsSlice';
import EnhancedNarrativeReportFlow from '../leo-reports/EnhancedNarrativeReportFlow';
import {
    LeoSubscriptionFlattenedRow,
    SubscriptionViewFilter,
} from '../../../types/subscriptions';
import SubscriptionsSummaryCards from './components/SubscriptionsSummaryCards';
import SubscriptionsTable from './components/SubscriptionsTable';
import {
    collectAttentionItems,
    flattenSubscriptionRows,
    formatSubscriptionAmount,
    formatSubscriptionDate,
    formatSubscriptionInterval,
    formatSubscriptionStatus,
} from './utils';

const FILTER_ITEMS: {
    key: SubscriptionViewFilter;
    label: string;
}[] = [
    { key: 'all', label: 'All rows' },
    { key: 'attention', label: 'Needs review' },
    { key: 'with-subscription', label: 'With subscription' },
    { key: 'without-subscription', label: 'Missing subscription' },
];

const LeoSubscriptionsPage = () => {
    const dispatch = useAppDispatch();
    const { data, loading, requestStatus, message } = useAppSelector(
        (state) => state.leoSubscriptions,
    );

    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] =
        useState<SubscriptionViewFilter>('all');
    const [isExporting, setIsExporting] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const fetchSubscriptions = async () => {
        try {
            await dispatch(tryToFetchLeoSubscriptions()).unwrap();
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const report = data?.data;
    const allRows = flattenSubscriptionRows(report?.items || []);
    const attentionItems = collectAttentionItems(report?.items || []);
    const activeSubscriptionsCount = Array.from(
        new Set(
            allRows
                .filter(
                    (row) =>
                        row.subscriptionId &&
                        row.subscriptionStatus?.toLowerCase() === 'active',
                )
                .map((row) => row.subscriptionId),
        ),
    ).length;

    const filterCounts: Record<SubscriptionViewFilter, number> = {
        all: allRows.length,
        attention: allRows.filter((row) => row.highlightRenewal).length,
        'with-subscription': allRows.filter((row) => Boolean(row.subscriptionId))
            .length,
        'without-subscription': allRows.filter((row) => !row.subscriptionId)
            .length,
    };

    const matchesFilter = (
        row: LeoSubscriptionFlattenedRow,
        filter: SubscriptionViewFilter,
    ) => {
        if (filter === 'attention') return row.highlightRenewal;
        if (filter === 'with-subscription') return Boolean(row.subscriptionId);
        if (filter === 'without-subscription') return !row.subscriptionId;
        return true;
    };

    const normalizedSearch = search.trim().toLowerCase();

    const filteredRows = allRows.filter((row) => {
        if (!matchesFilter(row, activeFilter)) return false;
        if (!normalizedSearch) return true;

        return [
            row.scope,
            row.userName,
            row.userEmail,
            row.propertyName,
            row.clientName,
            row.scenarioName,
            row.planName,
            row.subscriptionId,
            row.subscriptionStatus,
            row.interval,
        ].some((value) =>
            (value || '').toLowerCase().includes(normalizedSearch),
        );
    });

    const handleExport = async () => {
        if (!report || !filteredRows.length) return;

        setIsExporting(true);

        try {
            const workbook = XLSX.utils.book_new();

            const summarySheet = XLSX.utils.aoa_to_sheet([
                ['Report run date', formatSubscriptionDate(report.reportRunDate)],
                ['Aggregation ID', data?.aggregationId || 'Not set'],
                [
                    'Highlight threshold days',
                    report.highlightThresholdDays?.toString() || '0',
                ],
                ['Total users', report.totalUsers.toString()],
                ['Total properties', report.totalProperties.toString()],
                ['Total scenarios', report.totalScenarios.toString()],
                ['Active subscriptions', activeSubscriptionsCount.toString()],
                ['Exported rows', filteredRows.length.toString()],
            ]);

            summarySheet['!cols'] = [{ wch: 24 }, { wch: 42 }];

            const detailsSheet = XLSX.utils.json_to_sheet(
                filteredRows.map((row) => ({
                    Scope: row.scope,
                    'User name': row.userName || '',
                    'User email': row.userEmail || '',
                    Property: row.propertyName || '',
                    Client: row.clientName || '',
                    Scenario: row.scenarioName || '',
                    Plan: row.planName || '',
                    'Subscription ID': row.subscriptionId || '',
                    Status: formatSubscriptionStatus(row.subscriptionStatus),
                    Renewal: formatSubscriptionDate(row.subscriptionRenewalDate),
                    'Days until renewal':
                        row.daysUntilRenewal == null
                            ? ''
                            : row.daysUntilRenewal,
                    Amount: formatSubscriptionAmount(row.amount, row.currency),
                    Interval: formatSubscriptionInterval(row.interval),
                    Highlighted: row.highlightRenewal ? 'Yes' : 'No',
                    Reason: row.conditionalFormatting?.reason || '',
                })),
            );

            detailsSheet['!cols'] = [
                { wch: 14 },
                { wch: 18 },
                { wch: 34 },
                { wch: 28 },
                { wch: 24 },
                { wch: 24 },
                { wch: 28 },
                { wch: 32 },
                { wch: 14 },
                { wch: 16 },
                { wch: 18 },
                { wch: 16 },
                { wch: 16 },
                { wch: 12 },
                { wch: 38 },
            ];

            XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
            XLSX.utils.book_append_sheet(
                workbook,
                detailsSheet,
                'Subscriptions',
            );

            XLSX.writeFile(
                workbook,
                `leo-subscriptions-${new Date()
                    .toISOString()
                    .slice(0, 10)}.xlsx`,
            );
        } catch (error) {
            console.error(error);
        } finally {
            setIsExporting(false);
        }
    };

    const hasError = requestStatus === 'rejected' && !report;

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'>
                    <div className='flex flex-col'>
                        <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                            Leo Subscriptions
                        </h1>
                        <p className='text-sm text-gray-600 dark:text-gray-300'>
                            Review renewal dates, plan coverage and subscription
                            gaps across users, properties and scenarios.
                        </p>
                    </div>

                    <div className='flex flex-wrap items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => setReportOpen(true)}
                            className='inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black px-4 py-2 text-sm font-semibold text-[#00330F] dark:text-primary-darkMode transition hover:opacity-80'
                        >
                            <img src='/assets/reports/narrative.svg' alt='' className='h-5 w-5 dark:brightness-0 dark:invert' />
                            Enhanced Report
                        </button>
                        <button
                            type='button'
                            onClick={fetchSubscriptions}
                            disabled={loading}
                            className='inline-flex items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black px-4 py-2 text-sm font-semibold text-[#00330F] dark:text-primary-darkMode transition hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60'
                        >
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <button
                            type='button'
                            onClick={handleExport}
                            disabled={isExporting || !filteredRows.length}
                            className='inline-flex items-center justify-center rounded-xl bg-primary-default px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
                        >
                            {isExporting ? (
                                <>
                                    <Loading />
                                    Preparing file
                                </>
                            ) : (
                                <>
                                    Download Excel
                                    <img
                                        src='/assets/chat/export.svg'
                                        className='ml-3'
                                        alt='download excel'
                                    />
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {hasError ? (
                    <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-6'>
                        <p className='text-base font-semibold text-[#101010] dark:text-primary-darkMode'>
                            We couldn&apos;t load Leo subscriptions.
                        </p>
                        <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>
                            {message || 'Please try again.'}
                        </p>
                        <button
                            type='button'
                            onClick={fetchSubscriptions}
                            className='mt-4 inline-flex items-center justify-center rounded-xl bg-primary-default px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90'
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        <SubscriptionsSummaryCards
                            loading={loading && !report}
                            items={[
                                {
                                    label: 'Users',
                                    value: report?.totalUsers || 0,
                                    description:
                                        'Accounts included in the latest report',
                                    tone: 'primary',
                                },
                                {
                                    label: 'Properties',
                                    value: report?.totalProperties || 0,
                                    description:
                                        'Property-level subscription records',
                                    tone: 'soft',
                                },
                                {
                                    label: 'Scenarios',
                                    value: report?.totalScenarios || 0,
                                    description:
                                        'Scenario-level subscriptions tracked',
                                    tone: 'neutral',
                                },
                                {
                                    label: 'Active subscriptions',
                                    value: activeSubscriptionsCount,
                                    description:
                                        'Unique active subscription IDs in this view',
                                    tone: 'warm',
                                },
                            ]}
                        />

                        <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-4'>
                            <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                                <div className='flex flex-wrap gap-2'>
                                    <span className='inline-flex items-center rounded-full border border-[#dbe9d6] bg-[#f3fded] px-3 py-1 text-xs font-semibold text-[#00330F] dark:border-[#274330] dark:bg-[#102515] dark:text-[#dff6d4]'>
                                        Report run {formatSubscriptionDate(report?.reportRunDate)}
                                    </span>
                                    <span className='inline-flex items-center rounded-full border border-gray-200 dark:border-white/10 bg-[#FAFAFA] dark:bg-white/5 px-3 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200'>
                                        Highlight threshold{' '}
                                        {report?.highlightThresholdDays || 0} days
                                    </span>
                                    <span className='inline-flex items-center rounded-full border border-gray-200 dark:border-white/10 bg-[#FAFAFA] dark:bg-white/5 px-3 py-1 text-xs font-semibold text-gray-700 dark:text-gray-200 break-all'>
                                        Aggregation {data?.aggregationId || 'Not set'}
                                    </span>
                                </div>

                                <p className='text-xs text-gray-500 dark:text-gray-400'>
                                    Showing {filteredRows.length.toLocaleString()}{' '}
                                    of {allRows.length.toLocaleString()} rows
                                </p>
                            </div>
                        </div>

                        <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-4'>
                            <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                                <div>
                                    <p className='text-base font-semibold text-[#101010] dark:text-primary-darkMode'>
                                        Renewals to review
                                    </p>
                                    <p className='text-sm text-gray-500 dark:text-gray-400'>
                                        Subscriptions renewing within the current
                                        highlight window.
                                    </p>
                                </div>

                                <span className='inline-flex items-center rounded-full border border-[#ece3c1] bg-[#fffaf0] px-3 py-1 text-xs font-semibold text-[#6d5800] dark:border-[#5b4f2b] dark:bg-[#2b2417] dark:text-[#f5e0a8]'>
                                    {attentionItems.length.toLocaleString()}{' '}
                                    flagged
                                </span>
                            </div>

                            {attentionItems.length ? (
                                <div className='mt-4 grid grid-cols-1 gap-3 xl:grid-cols-3'>
                                    {attentionItems.slice(0, 6).map((item) => (
                                        <div
                                            key={item.id}
                                            className='rounded-2xl border border-[#ece3c1] bg-[#fffaf0] dark:border-[#5b4f2b] dark:bg-[#2b2417] p-4'
                                        >
                                            <div className='flex items-center justify-between gap-3'>
                                                <span className='inline-flex items-center rounded-full border border-[#e2d59b] bg-white/70 px-2.5 py-1 text-xs font-semibold text-[#6d5800] dark:border-[#7f6d31] dark:bg-black/20 dark:text-[#f5e0a8]'>
                                                    {item.scope}
                                                </span>
                                                <span className='text-xs font-semibold text-[#6d5800] dark:text-[#f5e0a8]'>
                                                    {item.daysUntilRenewal == null
                                                        ? 'No due date'
                                                        : `${item.daysUntilRenewal} day${
                                                              item.daysUntilRenewal ===
                                                              1
                                                                  ? ''
                                                                  : 's'
                                                          } left`}
                                                </span>
                                            </div>

                                            <p className='mt-3 text-base font-semibold text-[#101010] dark:text-primary-darkMode'>
                                                {item.propertyName}
                                            </p>
                                            <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                                                {item.scenarioName
                                                    ? `${item.userName} · ${item.scenarioName}`
                                                    : `${item.userName} · ${item.userEmail}`}
                                            </p>
                                            <p className='mt-3 text-sm font-medium text-[#101010] dark:text-primary-darkMode'>
                                                {formatSubscriptionDate(
                                                    item.subscriptionRenewalDate,
                                                )}
                                            </p>
                                            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                                {item.planName || 'No plan'} ·{' '}
                                                {formatSubscriptionAmount(
                                                    item.amount,
                                                    item.currency,
                                                )}
                                            </p>
                                            {item.reason ? (
                                                <p className='mt-3 text-xs font-medium text-[#6d5800] dark:text-[#f5e0a8]'>
                                                    {item.reason}
                                                </p>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='mt-4 rounded-2xl border border-[#dbe9d6] bg-[#f3fded] dark:border-[#274330] dark:bg-[#102515] p-4'>
                                    <p className='text-sm font-semibold text-[#00330F] dark:text-[#dff6d4]'>
                                        No renewals are currently inside the{' '}
                                        {report?.highlightThresholdDays || 0}-day
                                        highlight window.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className='rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-black p-4'>
                            <div className='flex flex-col gap-4'>
                                <div className='flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
                                    <div className='flex flex-1 flex-col gap-3 md:flex-row md:items-center'>
                                        <div className='relative w-full md:max-w-md'>
                                            <img
                                                alt='search'
                                                src='/svg/layout/search-icon.svg'
                                                className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 dark:invert'
                                            />
                                            <input
                                                value={search}
                                                onChange={(event) =>
                                                    setSearch(
                                                        event.target.value,
                                                    )
                                                }
                                                placeholder='Search user, property, client, plan or subscription ID'
                                                className='w-full rounded-xl border border-gray-200 dark:border-white/10 bg-[#FAFAFA] dark:bg-white/5 px-10 py-2.5 text-sm text-[#101010] dark:text-primary-darkMode outline-none transition focus:border-[#00330F]'
                                            />
                                        </div>
                                    </div>

                                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                                        Export downloads the current filtered
                                        view
                                    </p>
                                </div>

                                <div className='flex flex-wrap gap-2'>
                                    {FILTER_ITEMS.map((item) => {
                                        const isActive =
                                            item.key === activeFilter;

                                        return (
                                            <button
                                                key={item.key}
                                                type='button'
                                                onClick={() =>
                                                    setActiveFilter(item.key)
                                                }
                                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                                                    isActive
                                                        ? 'border-[#dbe9d6] bg-[#f3fded] text-[#00330F] dark:border-[#274330] dark:bg-[#102515] dark:text-[#dff6d4]'
                                                        : 'border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 hover:border-[#dbe9d6]'
                                                }`}
                                            >
                                                <span>{item.label}</span>
                                                <span className='rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 text-xs'>
                                                    {filterCounts[
                                                        item.key
                                                    ].toLocaleString()}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <SubscriptionsTable
                            rows={filteredRows}
                            loading={loading && !report}
                        />
                    </>
                )}
            </div>
            {reportOpen && (
                <EnhancedNarrativeReportFlow onClose={() => setReportOpen(false)} />
            )}
        </div>
    );
};

export default LeoSubscriptionsPage;
